import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  size?: "sm" | "md" | "lg";
}

const categoryColors: Record<string, string> = {
  "Success Story":       "bg-orange-500",
  "Startup":             "bg-green-600",
  "Innovation":          "bg-yellow-500",
  "Femme Entrepreneuse": "bg-pink-500",
  "Jeune Talent":        "bg-purple-500",
};

export default function ArticleCard({ article, size = "md" }: ArticleCardProps) {
  const { t } = useTranslation();

  const shareWhatsApp = (e: React.MouseEvent) => {
    e.preventDefault();
    const text = encodeURIComponent(`${article.title} - Lire sur Wazi Stories Afrique`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareLinkedIn = (e: React.MouseEvent) => {
    e.preventDefault();
    const url = article.originalUrl || article.sourceUrl;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
  };

  if (size === "sm") {
    return (
      <Link to={`/article/${article.id}`} className="group flex gap-3 cursor-pointer">
        <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className={`inline-block text-xs font-semibold text-white px-2 py-0.5 rounded-full mb-1 ${categoryColors[article.category] || "bg-gray-500"}`}>
            {article.category}
          </span>
          <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-orange-500 transition-colors">
            {article.title}
          </h4>
          <p className="text-xs text-gray-500 mt-1">{article.countryFlag} {article.country}</p>
          {article.source && (
            <p className="text-xs text-orange-500 font-medium mt-0.5 flex items-center gap-1">
              <i className="ri-links-line text-gray-400"></i>
              {article.source}
            </p>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group bg-white rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer block"
    >
      <div className="relative overflow-hidden" style={{ paddingBottom: "62.5%" }}>
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className={`text-xs font-semibold text-white px-3 py-1 rounded-full ${categoryColors[article.category] || "bg-gray-500"}`}>
            {article.category}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          <span>{article.countryFlag} {article.country}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300"></span>
          <span>{article.sector}</span>
          {article.readTime && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>{article.readTime} min</span>
            </>
          )}
        </div>
        <h3 className="font-bold text-gray-900 text-base leading-snug line-clamp-2 group-hover:text-orange-500 transition-colors mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
          {article.summary}
        </p>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center">
              <i className="ri-user-line text-orange-500 text-xs"></i>
            </div>
            <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">{article.entrepreneur}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={shareWhatsApp}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer"
              title={t("card.share.whatsapp")}
            >
              <i className="ri-whatsapp-line text-sm"></i>
            </button>
            <button
              onClick={shareLinkedIn}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
              title={t("card.share.linkedin")}
            >
              <i className="ri-linkedin-line text-sm"></i>
            </button>
          </div>
        </div>
        {article.source && article.sourceUrl && (
          <div className="mt-3 pt-2 border-t border-dashed border-gray-100 flex items-center gap-1.5">
            <i className="ri-links-line text-gray-400 text-xs"></i>
            <span className="text-xs text-gray-400">Source&nbsp;:</span>
            <a
              href={article.originalUrl || article.sourceUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-orange-500 hover:text-orange-600 hover:underline transition-colors cursor-pointer truncate"
            >
              {article.source}
            </a>
            <i className="ri-external-link-line text-gray-300 text-xs flex-shrink-0"></i>
          </div>
        )}
      </div>
    </Link>
  );
}
